import React, { useState } from "react";
import { Star, Send } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { VENUE_OPTIONS, saveToStorage } from "../mock/mock";

const ExperienceForm = () => {
  const { toast } = useToast();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", venue: "", title: "", experience: "" });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.experience) {
      toast({ title: "Missing details", description: "Please fill in your name, email, and experience.", variant: "destructive" });
      return;
    }
    saveToStorage("atom_experiences", { ...form, rating });
    toast({ title: "Thank you for sharing! \uD83C\uDF89", description: "Your experience has been added to the A.T.O.M. family." });
    setForm({ name: "", email: "", venue: "", title: "", experience: "" });
    setRating(0);
  };

  return (
    <form onSubmit={submit} className="bg-[#12100c] border border-white/8 rounded-2xl p-7 space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-[#c9bda9] mb-1.5 block text-sm">Full Name</Label>
          <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your name" className="bg-[#1a1710] border-white/10 text-[#f5f0e8] focus-visible:ring-[#e0b04c]" />
        </div>
        <div>
          <Label className="text-[#c9bda9] mb-1.5 block text-sm">Email Address</Label>
          <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" className="bg-[#1a1710] border-white/10 text-[#f5f0e8] focus-visible:ring-[#e0b04c]" />
        </div>
      </div>
      <div>
        <Label className="text-[#c9bda9] mb-1.5 block text-sm">Show / Venue Attended</Label>
        <Select value={form.venue} onValueChange={(v) => update("venue", v)}>
          <SelectTrigger className="bg-[#1a1710] border-white/10 text-[#f5f0e8]">
            <SelectValue placeholder="Select a venue (optional)" />
          </SelectTrigger>
          <SelectContent className="bg-[#1a1710] border-white/10 text-[#f5f0e8]">
            {VENUE_OPTIONS.map((v) => (<SelectItem key={v} value={v}>{v}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[#c9bda9] mb-1.5 block text-sm">Experience Title</Label>
        <Input value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="An unforgettable night" className="bg-[#1a1710] border-white/10 text-[#f5f0e8] focus-visible:ring-[#e0b04c]" />
      </div>
      <div>
        <Label className="text-[#c9bda9] mb-1.5 block text-sm">Share Your Experience</Label>
        <Textarea value={form.experience} onChange={(e) => update("experience", e.target.value.slice(0, 2000))} placeholder="Tell us how the show made you feel..." rows={4} className="bg-[#1a1710] border-white/10 text-[#f5f0e8] focus-visible:ring-[#e0b04c] resize-none" />
        <p className="text-right text-xs text-[#6f6656] mt-1">{form.experience.length}/2000</p>
      </div>
      <div>
        <Label className="text-[#c9bda9] mb-2 block text-sm">Rate the night (optional)</Label>
        <div className="flex gap-1.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <button key={s} type="button" onClick={() => setRating(s)} onMouseEnter={() => setHover(s)} onMouseLeave={() => setHover(0)} className="transition-transform hover:scale-110">
              <Star size={26} className={(hover || rating) >= s ? "fill-[#e0b04c] text-[#e0b04c]" : "text-[#4a4235]"} />
            </button>
          ))}
        </div>
      </div>
      <button type="submit" className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-b from-[#e8bc55] to-[#c99339] text-[#0c0a08] font-semibold px-8 py-3.5 rounded-md hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(224,176,76,0.35)] transition-all duration-300">
        <Send size={18} /> Share With The Band
      </button>
    </form>
  );
};

export default ExperienceForm;
